<template>
  <div class="expense-list-container">
    <PremiumTable
      title="Expenses"
      subtitle="Track and manage society financial outflows"
      add-label="Add"
      :show-view="true"
      :show-edit="true"
      :show-delete="true"
      @add="$router.push('/societyadmin/expense/add')"
      @view="$router.push('/societyadmin/expense/view/' + $event.id)"
      @edit="$router.push('/societyadmin/expense/edit/' + $event.id)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="expenses"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <!-- Table Controls (Moved from page header) -->
      <template #header-extra>
        <div class="table-actions-group">
          <!-- Total Expenses Stat Chip -->
          <div class="total-expense-chip">
            <v-icon size="15" color="#EF4444" class="mr-1">mdi-cash-multiple</v-icon>
            <span class="tec-label">Total</span>
            <span class="tec-value">₹{{ Number(total_expense).toLocaleString() }}</span>
          </div>

          <!-- Month Picker -->
          <PremiumDatePicker
            v-model="date"
            type="month"
            placeholder="Select Month"
            @change="getExpenses"
          />

          <!-- Export Button -->
          <button
            class="pt-export-btn"
            :style="{ background: brandColor }"
            title="Export to Excel"
            @click="exportDialog = true"
          >
            <v-icon size="16" color="white">mdi-download-outline</v-icon>
          </button>
        </div>
      </template>

      <!-- Custom Body Columns -->
      <template #col-title="{ item }">
        <v-tooltip
          bottom
          nudge-bottom="4"
          color="primary"
          max-width="400"
          :disabled="
            !(item.title && item.title.length > 30) &&
            !(item.note && item.note.length > 30)
          "
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="expense-title-cell" v-bind="attrs" v-on="on">
              <span class="expense-title-text">{{ item.title }}</span>
              <span class="expense-note-text" v-if="item.note">{{
                item.note
              }}</span>
            </div>
          </template>
          <div>
            <div style="font-weight: 700; margin-bottom: 2px">
              {{ item.title }}
            </div>
            <div
              v-if="item.note"
              style="font-size: 12px; opacity: 0.85; white-space: pre-line"
            >
              {{ item.note }}
            </div>
          </div>
        </v-tooltip>
      </template>

      <template #col-amount="{ item }">
        <span class="amount-text-red"
          >₹{{ Number(item.amount).toLocaleString() }}</span
        >
      </template>

      <template #col-date="{ item }">
        <span class="date-text">{{ item.date | formatDate }}</span>
      </template>

      <template #col-category_name="{ item }">
        <span class="cat-pill" v-if="item.category">
          {{ item.category.name }}
        </span>
        <span class="cat-pill unset" v-else>Uncategorized</span>
      </template>
    </PremiumTable>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Expense"
      message="Are you sure you want to delete this expense record?"
      confirm-text="Yes, Delete Expense"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteExpense"
    />

    <!-- ── Export Confirmation ──────────────── -->
    <ConfirmModal
      v-model="exportDialog"
      type="primary"
      :btnColor="brandColor"
      title="Export to Excel"
      message="Are you sure you want to export the expense report to an Excel file?"
      confirm-text="Yes, Export"
      @confirm="exportToExcel(); exportDialog = false;"
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumDatePicker from "../../common/PremiumDatePicker.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "ExpenseList",
  mixins: [filterMixin],
  components: { PremiumTable, PremiumDatePicker, ConfirmModal },
  data() {
    return {
      search: "",
      expenses: [],
      totalRecords: 0,
      total_expense: 0,
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: "",
      date: "",
      menu: false,
      page: 1,
      itemsPerPage: 10,
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      user: {},
      exportDialog: false,
    };
  },
  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },
    columns() {
      return [
        { key: "title", label: "Expense Details", width: "300px" },
        { key: "category_name", label: "Category", width: "160px" },
        { key: "amount", label: "Amount", width: "120px" },
        { key: "date", label: "Date", width: "140px" },
      ];
    },
  },
  watch: {
    page() {
      this.getExpenses();
    },
    itemsPerPage() {
      this.getExpenses();
    },
    date() {
      this.getExpenses();
    },
    search() {
      this.page = 1;
      this.getExpenses();
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    onSearch(val) {
      this.search = val;
    },
    onOptionsUpdate(opt) {
      this.options.sortBy = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getExpenses();
    },
    getExpenses() {
      this.loading = true;
      let sortBy = this.options.sortBy[0] || "";
      let sortDesc = this.options.sortDesc[0] || "";

      let year = "0",
        month = "0";
      if (this.date) {
        const parts = this.date.split("-");
        year = parts[0];
        month = parts[1];
      }

      const params = [
        `month=${month}`,
        `year=${year}`,
        `page=${this.page}`,
        `perPage=${this.itemsPerPage}`,
        `sortBy=${sortBy}`,
        `sortDesc=${sortDesc}`,
        `search=${this.search || ""}`,
      ].join("&");

      this.$axios
        .get("societyadmin/expenses?" + params)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          this.total_expense = data.total_expense;
          if (data.status) {
            this.expenses = data.expenses.data || data.expenses || [];
            this.totalRecords = data.total || (data.expenses && data.expenses.total) || 0;
          } else {
            this.showError(data.message);
            if (data.type == "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    exportToExcel() {
      let m = "0",
        y = "0";
      if (this.date) {
        m = moment(this.date, "YYYY-MM").get("month") + 1;
        y = moment(this.date, "YYYY-MM").get("year");
      }

      this.$axios
        .get("societyadmin/export/expenses", {
          params: {
            month: m,
            year: y,
            sortBy: this.options.sortBy[0],
            sortDesc: this.options.sortDesc[0],
            search: this.search,
          },
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Expenses_${moment().format("YYYY-MM-DD")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.showError("Failed to export data");
        });
    },
    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },
    deleteExpense() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/expenses/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getExpenses();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },
    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },
  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    
    // Initialize persistent filters for this module
    this.initFilters('expense');

    this.getExpenses();
  },
};
</script>

<style scoped>
.expense-list-container {
  padding: 24px;
}

/* Total Expense Chip (in table header) */
.total-expense-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: 38px;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.15);
  border-radius: 8px;
  white-space: nowrap;
}
.tec-label {
  font-size: 10px;
  font-weight: 700;
  color: #EF4444;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.tec-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13.5px;
  font-weight: 800;
  color: #EF4444;
}

.table-actions-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 768px) {
  .table-actions-group {
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
  .total-expense-chip {
    order: 3;
    width: 100%;
    justify-content: space-between;
  }
  .table-actions-group >>> .premium-date-wrap,
  .table-actions-group >>> .premium-datepicker-wrap {
    flex: 1;
    width: 100%;
  }
}
@media (max-width: 600px) {
  .expense-list-container {
    padding: 12px;
  }
  .table-actions-group {
    width: 100%;
  }
  .table-actions-group >>> .premium-date-trigger {
    min-width: 0 !important;
  }
}

.custom-date-trigger {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 12px;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--tx2);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 150px;
}
.custom-date-trigger:hover {
  border-color: var(--brand);
  transform: translateY(-1px);
}

.icon-action-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tx2);
  cursor: pointer;
  transition: all 0.2s;
}
.icon-action-btn:hover {
  border-color: var(--brand);
  color: var(--brand);
  transform: translateY(-1px);
}

/* ── Cells ── */
.expense-title-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}
.expense-title-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
}
.expense-note-text {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
}

.cat-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 8px;
  background: var(--surface2);
  color: var(--tx2);
  font-size: 12px;
  font-weight: 700;
  border: 1px solid var(--border);
}
.cat-pill.unset {
  opacity: 0.6;
  font-style: italic;
}

.amount-text-red {
  font-weight: 800;
  color: #ef4444;
  font-family: "JetBrains Mono", monospace;
  font-size: 14px;
}
.date-text {
  font-size: 13px;
  color: var(--tx3);
  font-weight: 600;
}
</style>
